/**
 * Mat label picker: search, select-all, copies, barcode/size modes, print grid rebuild.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'mat-label-print-selection';

  function qs(root, sel) {
    return (root || document).querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function visibleRows(section) {
    return qsa(section, '.mat-label-picker__row:not(.is-hidden)');
  }

  function allRows(section) {
    return qsa(section, '.mat-label-picker__row');
  }

  function getScreenGrid(section) {
    var screenSheet = qs(section, '.mat-label-sheet--screen');
    return screenSheet ? qs(screenSheet, '.mat-label-sheet__grid') : null;
  }

  function clampCopies(value) {
    return Math.min(9, Math.max(1, parseInt(value, 10) || 1));
  }

  function applyGlobalCopies(section) {
    var globalEl = qs(section, '[data-mat-label-global-copies]');
    if (!globalEl) return;
    var copies = clampCopies(globalEl.value);
    globalEl.value = copies;
    allRows(section).forEach(function (row) {
      var rowCopies = qs(row, '.mat-label-picker__copies');
      if (rowCopies) rowCopies.value = copies;
    });
    updateSummary(section);
    rebuildSheets(section);
  }

  function getPrintMode(section) {
    var includeBarcode = qs(section, '[data-mat-label-include-barcode]');
    var checked = includeBarcode ? includeBarcode.checked : true;
    var sizeEl = qs(section, '[data-mat-label-size]');
    var size = sizeEl ? sizeEl.value : 'both';
    return {
      includeBarcode: checked,
      size: size,
    };
  }

  function syncBarcodeUi(section) {
    var mode = getPrintMode(section);
    section.setAttribute('data-show-barcode-active', mode.includeBarcode ? 'true' : 'false');

    var sizeWrap = qs(section, '[data-mat-label-size-wrap]');
    if (sizeWrap) {
      sizeWrap.hidden = !mode.includeBarcode;
    }
  }

  function getRowTemplateHtml(row, templateClass) {
    var template = qs(row, templateClass);
    return template ? template.innerHTML : null;
  }

  function countLabelsForRow(row, mode) {
    var copies = clampCopies(qs(row, '.mat-label-picker__copies')?.value);

    if (!mode.includeBarcode) {
      return copies;
    }

    if (mode.size === 'both') {
      var has11 = row.getAttribute('data-has-11x14') === 'true';
      var has16 = row.getAttribute('data-has-16x24') === 'true';
      var perCopy = (has11 ? 1 : 0) + (has16 ? 1 : 0);
      return perCopy * copies;
    }

    if (mode.size === '11x14' && row.getAttribute('data-has-11x14') === 'true') {
      return copies;
    }

    if (mode.size === '16x24' && row.getAttribute('data-has-16x24') === 'true') {
      return copies;
    }

    if (mode.size === '5x7' && row.getAttribute('data-has-5x7') === 'true') {
      return copies;
    }

    return 0;
  }

  function updateSummary(section) {
    var summary = qs(section, '[data-mat-label-summary]');
    if (!summary) return;

    var mode = getPrintMode(section);
    var labelCount = 0;
    var productHandles = {};

    allRows(section).forEach(function (row) {
      var cb = qs(row, '.mat-label-picker__check');
      if (!cb || !cb.checked) return;
      labelCount += countLabelsForRow(row, mode);
      productHandles[row.getAttribute('data-product-handle') || ''] = true;
    });

    var productCount = Object.keys(productHandles).filter(Boolean).length;
    summary.textContent =
      labelCount +
      ' label' +
      (labelCount === 1 ? '' : 's') +
      ' · ' +
      productCount +
      ' product' +
      (productCount === 1 ? '' : 's');
  }

  function applySearch(section) {
    var query = (qs(section, '[data-mat-label-search]')?.value || '').trim().toLowerCase();
    allRows(section).forEach(function (row) {
      var title = row.getAttribute('data-search-title') || '';
      row.classList.toggle('is-hidden', query.length > 0 && title.indexOf(query) === -1);
    });
  }

  function sortPickerList(section) {
    var list = qs(section, '[data-mat-label-picker-list]');
    var sortEl = qs(section, '[data-mat-label-sort]');
    if (!list) return;

    var mode = sortEl ? sortEl.value : 'title-asc';
    var rows = qsa(list, '.mat-label-picker__row');

    rows.sort(function (a, b) {
      if (mode === 'collection') {
        var oa = parseInt(a.getAttribute('data-collection-order'), 10) || 0;
        var ob = parseInt(b.getAttribute('data-collection-order'), 10) || 0;
        return oa - ob;
      }

      var ta = a.getAttribute('data-search-title') || '';
      var tb = b.getAttribute('data-search-title') || '';
      var cmp = ta.localeCompare(tb, undefined, { sensitivity: 'base' });
      return mode === 'title-desc' ? -cmp : cmp;
    });

    rows.forEach(function (row) {
      list.appendChild(row);
    });
  }

  function saveSelection(section) {
    try {
      var mode = getPrintMode(section);
      var state = {
        mode: mode,
        sort: qs(section, '[data-mat-label-sort]')?.value,
        globalCopies: qs(section, '[data-mat-label-global-copies]')?.value,
        rows: allRows(section).map(function (row) {
          return {
            productId: row.getAttribute('data-product-id'),
            variantId: row.getAttribute('data-variant-id'),
            checked: qs(row, '.mat-label-picker__check')?.checked,
            copies: qs(row, '.mat-label-picker__copies')?.value,
          };
        }),
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* ignore */
    }
  }

  function restoreSelection(section) {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var state = JSON.parse(raw);

      if (state.mode) {
        var includeBarcode = qs(section, '[data-mat-label-include-barcode]');
        if (includeBarcode && typeof state.mode.includeBarcode === 'boolean') {
          includeBarcode.checked = state.mode.includeBarcode;
        }
        var sizeEl = qs(section, '[data-mat-label-size]');
        if (sizeEl && state.mode.size) {
          sizeEl.value = state.mode.size;
        }
      }

      var sortEl = qs(section, '[data-mat-label-sort]');
      if (sortEl && state.sort) {
        sortEl.value = state.sort;
      }

      var globalEl = qs(section, '[data-mat-label-global-copies]');
      if (globalEl && state.globalCopies) {
        globalEl.value = clampCopies(state.globalCopies);
      }

      var rows = Array.isArray(state) ? state : state.rows;
      if (!Array.isArray(rows)) return;

      rows.forEach(function (item) {
        var row =
          qs(section, '.mat-label-picker__row[data-product-id="' + item.productId + '"]') ||
          qs(section, '.mat-label-picker__row[data-variant-id="' + item.variantId + '"]');
        if (!row) return;
        var cb = qs(row, '.mat-label-picker__check');
        if (cb && typeof item.checked === 'boolean') cb.checked = item.checked;
        var copies = qs(row, '.mat-label-picker__copies');
        if (copies && item.copies) copies.value = item.copies;
      });
    } catch (e) {
      /* ignore */
    }
  }

  function collectSelectedTemplates(section) {
    syncBarcodeUi(section);
    var mode = getPrintMode(section);
    var items = [];

    allRows(section).forEach(function (row) {
      var cb = qs(row, '.mat-label-picker__check');
      if (!cb || !cb.checked) return;

      var copies = clampCopies(qs(row, '.mat-label-picker__copies')?.value);

      for (var c = 0; c < copies; c++) {
        if (!mode.includeBarcode) {
          var textTemplate = getRowTemplateHtml(row, '.mat-label__cell-template--text');
          if (textTemplate) items.push(textTemplate);
          continue;
        }

        if (mode.size === 'both') {
          var left = getRowTemplateHtml(row, '.mat-label__cell-template--11x14') || '';
          var right = getRowTemplateHtml(row, '.mat-label__cell-template--16x24') || '';
          items.push(left);
          items.push(right);
          continue;
        }

        if (mode.size === '11x14') {
          var small = getRowTemplateHtml(row, '.mat-label__cell-template--11x14');
          if (small) items.push(small);
          continue;
        }

        if (mode.size === '16x24') {
          var large = getRowTemplateHtml(row, '.mat-label__cell-template--16x24');
          if (large) items.push(large);
          continue;
        }

        if (mode.size === '5x7') {
          var card = getRowTemplateHtml(row, '.mat-label__cell-template--5x7');
          if (card) items.push(card);
        }
      }
    });

    return items;
  }

  function clearGridSlots(grid) {
    if (!grid) return;
    qsa(grid, '.mat-label-sheet__slot').forEach(function (slot) {
      slot.innerHTML = '';
    });
  }

  function fillGridSlots(grid, items) {
    if (!grid) return;
    var slots = qsa(grid, '.mat-label-sheet__slot');
    items.forEach(function (html, index) {
      if (slots[index]) {
        slots[index].innerHTML = html;
      }
    });
  }

  function renderBarcodesIn(section, root) {
    if (window.MatLabelBarcodes && root) {
      window.MatLabelBarcodes.render(root, getBarcodeOptions(section));
    }
  }

  function rebuildSheets(section) {
    var items = collectSelectedTemplates(section);
    var screenGrid = getScreenGrid(section);
    if (!screenGrid) return;

    clearGridSlots(screenGrid);
    fillGridSlots(screenGrid, items);
    renderBarcodesIn(section, qs(section, '.mat-label-sheet--screen'));
  }

  function getBarcodeOptions(section) {
    return {
      height: parseInt(section.getAttribute('data-barcode-height-px'), 10) || 32,
      format: section.getAttribute('data-barcode-format') || 'CODE128',
    };
  }

  function createPrintSheet(section, sheetClass, slotsPerSheet, showRegmarks) {
    var sheet = document.createElement('div');
    sheet.className = 'mat-label-sheet mat-label-sheet--portal ' + sheetClass;

    var grid = document.createElement('div');
    grid.className = 'mat-label-sheet__grid';
    for (var s = 0; s < slotsPerSheet; s++) {
      var slot = document.createElement('div');
      slot.className = 'mat-label-sheet__slot';
      grid.appendChild(slot);
    }
    sheet.appendChild(grid);

    if (showRegmarks) {
      var overlay = document.createElement('div');
      overlay.className = 'mat-label-sheet__regmarks';
      overlay.setAttribute('aria-hidden', 'true');
      ['tl', 'tr', 'bl', 'br'].forEach(function (corner) {
        var mark = document.createElement('span');
        mark.className = 'mat-label-sheet__regmark mat-label-sheet__regmark--' + corner;
        overlay.appendChild(mark);
      });
      sheet.appendChild(overlay);
    }

    return sheet;
  }

  function getPrintPortal() {
    var portal = document.getElementById('mat-label-print-portal');
    if (!portal) {
      portal = document.createElement('div');
      portal.id = 'mat-label-print-portal';
      portal.setAttribute('aria-hidden', 'true');
      document.body.appendChild(portal);
    }
    return portal;
  }

  function syncPrintPortal(section) {
    rebuildSheets(section);

    var items = collectSelectedTemplates(section);
    var portal = getPrintPortal();
    portal.innerHTML = '';

    if (items.length === 0) {
      document.body.classList.remove('mat-label-printing');
      return false;
    }

    var slotsPerSheet = parseInt(section.getAttribute('data-slots-per-sheet'), 10) || 10;
    var sheetClass = section.getAttribute('data-sheet-class') || 'mat-label-sheet--avery_8163';
    var showRegmarks = section.getAttribute('data-registration-marks') === 'true';
    var showBarcode = section.getAttribute('data-show-barcode-active') || 'true';

    portal.setAttribute('data-show-barcode-active', showBarcode);

    var sheetIndex = 0;
    var slotIndex = 0;
    var currentSheet = null;
    var currentGrid = null;

    items.forEach(function (html) {
      if (!currentSheet || slotIndex >= slotsPerSheet) {
        currentSheet = createPrintSheet(section, sheetClass, slotsPerSheet, showRegmarks && sheetIndex === 0);
        portal.appendChild(currentSheet);
        currentGrid = qs(currentSheet, '.mat-label-sheet__grid');
        sheetIndex++;
        slotIndex = 0;
      }
      var slots = qsa(currentGrid, '.mat-label-sheet__slot');
      if (slots[slotIndex]) {
        slots[slotIndex].innerHTML = html;
      }
      slotIndex++;
    });

    renderBarcodesIn(section, portal);
    document.body.classList.add('mat-label-printing');
    return true;
  }

  function prepareForPrint(section) {
    return syncPrintPortal(section);
  }

  function cleanupAfterPrint() {
    document.body.classList.remove('mat-label-printing');
    var portal = document.getElementById('mat-label-print-portal');
    if (portal) portal.innerHTML = '';
  }

  function handlePrint(section) {
    if (!prepareForPrint(section)) {
      window.alert('Select at least one product to print.');
      return;
    }
    saveSelection(section);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        window.print();
      });
    });
  }

  function loadCatalogPages(section) {
    return new Promise(function (resolve) {
      var meta = qs(section, '[data-mat-label-catalog-meta]');
      if (!meta) {
        resolve();
        return;
      }

      var handle = meta.getAttribute('data-catalog-handle');
      var totalPages = parseInt(meta.getAttribute('data-catalog-pages'), 10) || 1;
      if (!handle || totalPages <= 1) {
        resolve();
        return;
      }

      var list = qs(section, '[data-mat-label-picker-list]');
      if (!list) {
        resolve();
        return;
      }

      var status = qs(section, '[data-mat-label-catalog-status]');
      if (status) {
        status.hidden = false;
        status.textContent = 'Loading catalog (page 1 of ' + totalPages + ')…';
      }

      var seenHandles = {};
      allRows(section).forEach(function (row) {
        var productHandle = row.getAttribute('data-product-handle');
        if (productHandle) {
          seenHandles[productHandle] = true;
        }
      });

      var page = 2;

      function fetchNext() {
        if (page > totalPages) {
          if (status) {
            status.hidden = true;
            status.textContent = '';
            status.classList.remove('mat-label-print__warning');
          }
          resolve();
          return;
        }

        if (status) {
          status.textContent = 'Loading catalog (page ' + page + ' of ' + totalPages + ')…';
        }

        var url = new URL(window.location.href);
        url.searchParams.set('section_id', 'mat-label-print');
        url.searchParams.set('page', String(page));
        if (!url.searchParams.get('collection')) {
          url.searchParams.set('collection', handle);
        }

        fetch(url.toString())
          .then(function (res) {
            if (!res.ok) {
              throw new Error('HTTP ' + res.status);
            }
            return res.text();
          })
          .then(function (html) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');
            var fetchedRows = qsa(doc, '.mat-label-picker__row');
            fetchedRows.forEach(function (row) {
              var productHandle = row.getAttribute('data-product-handle');
              if (productHandle && seenHandles[productHandle]) {
                return;
              }
              if (productHandle) {
                seenHandles[productHandle] = true;
              }
              list.appendChild(document.importNode(row, true));
            });
            page += 1;
            fetchNext();
          })
          .catch(function () {
            if (status) {
              status.hidden = false;
              status.textContent =
                'Loaded the first 50 products only. Refresh to retry loading the full catalog.';
              status.classList.add('mat-label-print__warning');
            }
            resolve();
          });
      }

      fetchNext();
    });
  }

  function initPickerState(section) {
    restoreSelection(section);
    syncBarcodeUi(section);
    sortPickerList(section);
    if (!sessionStorage.getItem(STORAGE_KEY)) {
      applyGlobalCopies(section);
    }
    applySearch(section);
    updateSummary(section);
    rebuildSheets(section);
  }

  function bindSection(section) {
    if (!section || section.getAttribute('data-mat-label-init') === 'true') return;
    section.setAttribute('data-mat-label-init', 'true');

    loadCatalogPages(section).then(function () {
      initPickerState(section);
    });

    var search = qs(section, '[data-mat-label-search]');
    if (search) {
      search.addEventListener('input', function () {
        applySearch(section);
      });
    }

    var globalCopies = qs(section, '[data-mat-label-global-copies]');
    if (globalCopies) {
      globalCopies.addEventListener('change', function () {
        applyGlobalCopies(section);
        saveSelection(section);
      });
      globalCopies.addEventListener('input', function () {
        applyGlobalCopies(section);
      });
    }

    var sortSelect = qs(section, '[data-mat-label-sort]');
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        sortPickerList(section);
        saveSelection(section);
      });
    }

    var selectAll = qs(section, '[data-mat-label-select-all]');
    if (selectAll) {
      selectAll.addEventListener('click', function () {
        visibleRows(section).forEach(function (row) {
          var cb = qs(row, '.mat-label-picker__check');
          if (cb) cb.checked = true;
        });
        updateSummary(section);
        rebuildSheets(section);
      });
    }

    var clearAll = qs(section, '[data-mat-label-clear-all]');
    if (clearAll) {
      clearAll.addEventListener('click', function () {
        allRows(section).forEach(function (row) {
          var cb = qs(row, '.mat-label-picker__check');
          if (cb) cb.checked = false;
        });
        updateSummary(section);
        rebuildSheets(section);
      });
    }

    section.addEventListener('change', function (e) {
      if (
        e.target.matches('.mat-label-picker__check') ||
        e.target.matches('.mat-label-picker__copies') ||
        e.target.matches('[data-mat-label-preset]') ||
        e.target.matches('[data-mat-label-include-barcode]') ||
        e.target.matches('[data-mat-label-size]')
      ) {
        if (e.target.matches('[data-mat-label-include-barcode]')) {
          syncBarcodeUi(section);
        }

        if (e.target.matches('[data-mat-label-preset]')) {
          var preset = e.target.value;
          section.setAttribute('data-sheet-class', 'mat-label-sheet--' + preset);
          section.setAttribute(
            'data-slots-per-sheet',
            e.target.selectedOptions[0]?.getAttribute('data-slots') || '10'
          );
          var screenSheet = qs(section, '.mat-label-sheet--screen');
          if (screenSheet) {
            screenSheet.className =
              'mat-label-sheet mat-label-sheet--screen mat-label-sheet--' + preset;
            var slotsPerSheet = parseInt(
              e.target.selectedOptions[0]?.getAttribute('data-slots') || '10',
              10
            );
            var grid = qs(screenSheet, '.mat-label-sheet__grid');
            if (grid) {
              var existing = qsa(grid, '.mat-label-sheet__slot').length;
              if (existing !== slotsPerSheet) {
                grid.innerHTML = '';
                for (var i = 0; i < slotsPerSheet; i++) {
                  var slot = document.createElement('div');
                  slot.className = 'mat-label-sheet__slot';
                  grid.appendChild(slot);
                }
              }
            }
          }
        }

        updateSummary(section);
        rebuildSheets(section);
      }
    });

    var printBtn = qs(section, '[data-mat-label-print]');
    if (printBtn) {
      printBtn.addEventListener('click', function () {
        handlePrint(section);
      });
    }

    if (!window.__matLabelPrintHooks) {
      window.__matLabelPrintHooks = true;
      window.addEventListener('beforeprint', function () {
        qsa(document, '[data-mat-label-section]').forEach(function (s) {
          syncPrintPortal(s);
        });
      });
      window.addEventListener('afterprint', cleanupAfterPrint);
    }

    var addBtn = qs(section, '[data-mat-label-add-handle]');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var input = qs(section, '[data-mat-label-handle-input]');
        var handle = (input?.value || '').trim().toLowerCase();
        if (!handle) return;

        var existing = qs(section, '.mat-label-picker__row[data-product-handle="' + handle + '"]');
        if (existing) {
          var cb = qs(existing, '.mat-label-picker__check');
          if (cb) cb.checked = true;
          existing.scrollIntoView({ block: 'nearest' });
          if (input) input.value = '';
          updateSummary(section);
          rebuildSheets(section);
          return;
        }

        var params = new URLSearchParams(window.location.search);
        var current = params.get('select') || params.get('handles') || '';
        var handles = current
          ? current.split(',').map(function (h) {
              return h.trim().toLowerCase();
            })
          : [];
        if (handles.indexOf(handle) === -1) {
          handles.push(handle);
        }
        params.delete('handles');
        params.set('select', handles.filter(Boolean).join(','));
        window.location.search = params.toString();
      });
    }
  }

  function init() {
    qsa(document, '[data-mat-label-section]').forEach(bindSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
